// Fetches private Storage objects with the caller's token and hands them to the
// DOM as blob: URLs. Anything under users/ is readable only when authenticated,
// so a plain <img src> — which never sends a token — cannot render it.

import { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { getBlob, ref } from 'firebase/storage';
import { auth, storage } from './firebase';

const cacheLimit = 300;
// How long a failed path is left alone. A thumbnail is generated by a backend
// trigger, so a fresh upload 404s for a few seconds and must be retried.
const negativeTtl = 30000;

// path → blob: URL. Entries never go stale: every upload gets a unique name.
const cache = new Map();
// path → when reading it last failed
const failures = new Map();
// path → in-flight promise, so a list of 60 rows fetches each file once
const pending = new Map();
// path → how many mounted hooks are showing it, so eviction cannot revoke a
// URL that is still in a style attribute — that would blank the element for
// good, since nothing would re-request it.
const showing = new Map();
// Bumped on every sign-out, so replies to the previous account are dropped
let generation = 0;

const remember = (path, url) => {
  cache.set(path, url);
  if (cache.size <= cacheLimit) return;

  for (const key of cache.keys()) {
    if (cache.size <= cacheLimit) break;
    // Never the entry just added: whoever asked for it is about to get this
    // URL back, and a revoked one renders as a blank element for good.
    if (key === path || showing.get(key)) continue;
    URL.revokeObjectURL(cache.get(key));
    cache.delete(key);
  }
};

// ms left before a failed path is worth reading again, 0 when it is due
const cooldownLeft = (path) => {
  const failedAt = failures.get(path);
  if (failedAt === undefined) return 0;

  const left = negativeTtl - (Date.now() - failedAt);
  if (left > 0) return left;

  failures.delete(path);
  return 0;
};

const loadImage = (path) => {
  const inFlight = pending.get(path);
  if (inFlight) return inFlight;

  const startedAt = generation;
  const read = async () => {
    try {
      const blob = await getBlob(ref(storage, path));
      const url = URL.createObjectURL(blob);

      // Signed out while this was in flight: these bytes belong to whoever
      // asked for them, so drop them rather than seed the next session.
      if (startedAt !== generation) {
        URL.revokeObjectURL(url);
        return '';
      }

      remember(path, url);
      return url;
    } catch (error) {
      // Every cause looks the same on screen — a placeholder. Say which it was:
      // a thumbnail the trigger has not made yet, a denial, a CORS-blocked read.
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Storage: failed to read ${path}`, error);
      }

      if (startedAt === generation) failures.set(path, Date.now());
      return '';
    } finally {
      // Only ours: clearCache() empties the map, so a reply from the previous
      // session must not evict the entry a new one has just put there.
      if (pending.get(path) === promise) pending.delete(path);
    }
  };

  // Deferred by a microtask so the entry is in the map before the body — and
  // its finally — can run, even when reading the path throws synchronously.
  const promise = Promise.resolve().then(read);
  pending.set(path, promise);
  return promise;
};

const clearCache = () => {
  generation += 1;
  cache.forEach((url) => url && URL.revokeObjectURL(url));
  cache.clear();
  failures.clear();
  pending.clear();
};

let authUid = auth.currentUser?.uid ?? null;
const authListeners = new Set();

// Signing out must not leave other people's photos in this tab's memory, and
// signing in has to re-run the hooks that gave up while there was no token.
onAuthStateChanged(auth, (user) => {
  const nextUid = user?.uid ?? null;
  if (nextUid === authUid) return;

  authUid = nextUid;
  clearCache();
  authListeners.forEach((notify) => notify());
});

const useAuthUid = () => {
  const [value, setValue] = useState(authUid);

  useEffect(() => {
    const sync = () => setValue(authUid);
    authListeners.add(sync);
    sync();
    return () => authListeners.delete(sync);
  }, []);

  return value;
};

const idle = { url: '', failed: false };
// Enough to outlast a slow thumbnail trigger, few enough that a path nobody is
// allowed to read is not polled for as long as the element stays on screen.
const maxRetries = 3;

// Storage path → { url, failed }: '' until the bytes arrive, failed once the
// read is known to be impossible for now. Callers that only display the image
// want useStorageImage; failed is for deciding whether to try something else.
// enabled=false skips the fetch entirely — for bytes nobody is looking at yet.
export const useStorageImageState = (path, enabled = true) => {
  const uid = useAuthUid();
  const [state, setState] = useState(() =>
    path && enabled && cache.has(path)
      ? { url: cache.get(path), failed: false }
      : idle,
  );
  // Bumping this re-runs the effect; the ref survives that re-run, so each
  // path gets its own budget of retries rather than one shared counter.
  const [attempt, setAttempt] = useState(0);
  const attemptsRef = useRef({ key: null, count: 0 });

  useEffect(() => {
    if (!path || !uid || !enabled) {
      setState(idle);
      return undefined;
    }

    showing.set(path, (showing.get(path) || 0) + 1);
    let active = true;
    let retryTimer;

    // Signing in as someone else clears the failures, so it deserves a fresh
    // budget too — the denial that used up the last one no longer applies.
    const key = `${uid}:${path}`;
    if (attemptsRef.current.key !== key)
      attemptsRef.current = { key, count: 0 };

    // A failed read usually means a thumbnail whose backend trigger has not
    // finished yet, so come back for it — nothing else would, and the element
    // would keep showing a placeholder until it happens to remount.
    const retryLater = (delay) => {
      if (attemptsRef.current.count >= maxRetries) return;

      retryTimer = setTimeout(() => {
        if (!active) return;
        attemptsRef.current.count += 1;
        setAttempt((n) => n + 1);
      }, delay);
    };

    const cooldown = cooldownLeft(path);

    if (cache.has(path)) {
      setState({ url: cache.get(path), failed: false });
    } else if (cooldown) {
      setState({ url: '', failed: true });
      retryLater(cooldown);
    } else {
      setState(idle);
      (async () => {
        const loaded = await loadImage(path);
        if (!active) return;

        setState({ url: loaded, failed: !loaded });
        if (!loaded) retryLater(negativeTtl);
      })();
    }

    return () => {
      active = false;
      clearTimeout(retryTimer);
      const left = (showing.get(path) || 1) - 1;
      if (left > 0) showing.set(path, left);
      else showing.delete(path);
    };
  }, [path, uid, enabled, attempt]);

  return state;
};

// Storage path → blob: URL, '' until it arrives. A cached path skips the wait.
export const useStorageImage = (path, enabled = true) =>
  useStorageImageState(path, enabled).url;
